import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code2,
  Link2, Image as ImageIcon, Undo2, Redo2,
  AlignLeft, AlignCenter, AlignRight,
  RemoveFormatting, Loader2, Eye, EyeOff,
} from "lucide-react";

/**
 * Lightweight HTML WYSIWYG editor built on contentEditable + execCommand.
 * Output is HTML (string). Caller is responsible for trusting/sanitizing it
 * — used by admins only.
 */
export default function RichTextEditor({
  value,
  onChange,
  onImageUpload,
  placeholder = "Start writing your post…",
  minHeight = 360,
}) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  // Keep the DOM in sync only when external value diverges (preserves caret).
  useEffect(() => {
    if (!ref.current) return;
    const next = value || "";
    if (ref.current.innerHTML !== next) {
      ref.current.innerHTML = next;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback(() => {
    if (!ref.current) return;
    onChange(ref.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => {
    if (!ref.current) return;
    ref.current.focus();
    document.execCommand(cmd, false, val);
    emit();
  };

  const block = (tag) => exec("formatBlock", `<${tag}>`);

  const handleLink = () => {
    const url = window.prompt("Paste link URL (include https://)");
    if (!url) return;
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch (_) {
      alert("Invalid URL");
      return;
    }
    exec("createLink", url);
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!onImageUpload) {
      alert("Image upload not configured");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const url = await onImageUpload(file);
      if (url) exec("insertImage", url);
    } catch (err) {
      alert(err?.message || "Image upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onPaste = (e) => {
    // Strip rich formatting from pasted text, keeping it predictable.
    // Power users can still use the toolbar to format.
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, text);
    emit();
  };

  const toggleHtml = () => {
    if (showHtml) {
      // switching from HTML source back to WYSIWYG — push the textarea value into the DOM
      if (ref.current) ref.current.innerHTML = value || "";
    }
    setShowHtml((v) => !v);
  };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="rich-text-editor">
      <div className="border-b border-slate-200 bg-slate-50 p-1.5 flex flex-wrap items-center gap-0.5 sticky top-0 z-10">
        <Group>
          <Btn title="Undo (Ctrl+Z)" onClick={() => exec("undo")}><Undo2 className="w-4 h-4" /></Btn>
          <Btn title="Redo (Ctrl+Y)" onClick={() => exec("redo")}><Redo2 className="w-4 h-4" /></Btn>
        </Group>
        <Sep />
        <Group>
          <Btn title="Heading 1" onClick={() => block("h1")}><Heading1 className="w-4 h-4" /></Btn>
          <Btn title="Heading 2" onClick={() => block("h2")}><Heading2 className="w-4 h-4" /></Btn>
          <Btn title="Heading 3" onClick={() => block("h3")}><Heading3 className="w-4 h-4" /></Btn>
          <Btn title="Paragraph" onClick={() => block("p")}><span className="text-[11px] font-semibold px-0.5">P</span></Btn>
        </Group>
        <Sep />
        <Group>
          <Btn title="Bold (Ctrl+B)" onClick={() => exec("bold")}><Bold className="w-4 h-4" /></Btn>
          <Btn title="Italic (Ctrl+I)" onClick={() => exec("italic")}><Italic className="w-4 h-4" /></Btn>
          <Btn title="Underline (Ctrl+U)" onClick={() => exec("underline")}><UnderlineIcon className="w-4 h-4" /></Btn>
          <Btn title="Strikethrough" onClick={() => exec("strikeThrough")}><Strikethrough className="w-4 h-4" /></Btn>
        </Group>
        <Sep />
        <Group>
          <Btn title="Bulleted list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" /></Btn>
          <Btn title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" /></Btn>
          <Btn title="Blockquote" onClick={() => block("blockquote")}><Quote className="w-4 h-4" /></Btn>
          <Btn title="Code block" onClick={() => block("pre")}><Code2 className="w-4 h-4" /></Btn>
        </Group>
        <Sep />
        <Group>
          <Btn title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="w-4 h-4" /></Btn>
          <Btn title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter className="w-4 h-4" /></Btn>
          <Btn title="Align right" onClick={() => exec("justifyRight")}><AlignRight className="w-4 h-4" /></Btn>
        </Group>
        <Sep />
        <Group>
          <Btn title="Insert link" onClick={handleLink}><Link2 className="w-4 h-4" /></Btn>
          <label
            title="Insert image"
            className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-slate-200 cursor-pointer"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleImageFile}
              disabled={uploading}
            />
          </label>
          <Btn title="Clear formatting" onClick={() => exec("removeFormat")}><RemoveFormatting className="w-4 h-4" /></Btn>
        </Group>
        <Sep />
        <Btn title={showHtml ? "Visual editor" : "View HTML source"} onClick={toggleHtml}>
          {showHtml ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Btn>
      </div>

      {showHtml ? (
        <textarea
          className="w-full p-4 font-mono text-xs text-slate-800 outline-none resize-y"
          style={{ minHeight }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          data-testid="rich-text-html"
        />
      ) : (
        <div
          ref={ref}
          contentEditable
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onPaste={onPaste}
          className="nc-rte prose prose-slate max-w-none p-4 focus:outline-none"
          style={{ minHeight }}
          data-placeholder={placeholder}
          data-testid="rich-text-content"
        />
      )}
    </div>
  );
}

function Btn({ children, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex items-center justify-center w-8 h-8 rounded text-slate-700 hover:bg-slate-200 hover:text-slate-900 transition-colors"
    >
      {children}
    </button>
  );
}

function Group({ children }) {
  return <div className="inline-flex items-center">{children}</div>;
}

function Sep() {
  return <div className="w-px h-5 bg-slate-300 mx-1" aria-hidden />;
}
